import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, CircularProgress, FormControlLabel, IconButton, Menu, MenuItem, TextField, css, Switch, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OfflinePinIcon from '@mui/icons-material/OfflinePin';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { useSessionStore } from 'session/useSessionStore';
import { Grid } from 'components/icons/components';
import { useSeismicGraticule } from '../hooks/useSeismicGraticule';
import { ILineString } from '../models/interfaces/ILineString';
import { useLine3DNavigationStore } from '../stores/useLine3DNavigationStore';
import { LineType } from '../models/enums/LineType';
import { useSeismicStore } from '../stores/useSeismicStore';

const inputStyle = css({
    '& .MuiFormControl-root': {
        '& .MuiOutlinedInput-notchedOutline': {
            '& legend': {
                width: '48px',
                borderWidth: '1px'
            }
        }
    },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            borderColor: 'white',
            borderWidth: '1px',
            borderRadius: '5px'
        }
    }
});

export function SeismicGraticule({ dataGeometryLine }:Props) {
    const { isEnable, toggle, line, stepX, stepY } = useSeismicGraticule(dataGeometryLine);
    const [ openDetail, setOpenDetail ] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const iconButtonRef = useRef(null);
    const lineType = useLine3DNavigationStore(state => state.lineType);
    const samplesPerTrace = useSeismicStore(state => state.surveyMetadata?.Header.SamplesPerTrace);
    const {tenantConfig} = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration
    }));
    const { t } = useTranslation();

    const className = (isEnable) ? 'active' : '';

    const handleClose = () => setOpenDetail(false);

    const handleMenu = (event: React.MouseEvent) => {
        setOpenDetail(true);
        setAnchorEl(iconButtonRef.current);
    };

    useEffect(() => {
        if (!!samplesPerTrace && samplesPerTrace > 20000) {
            stepY.set(4000);
        }
    }, [samplesPerTrace]);

    const [xStepInput, setXStepInput] = useState(stepX.value);
    const [yStepInput, setYStepInput] = useState(stepY.value);

    const applySteps = useCallback(() => {
        let xStepValue = xStepInput;
        let yStepValue = yStepInput;

        if (isNaN(xStepValue) || xStepValue < 10) {
            xStepValue = 10;
            setXStepInput(xStepValue);
        }
        if (isNaN(yStepValue) || yStepValue < 10) {
            yStepValue = 10;
            setYStepInput(yStepValue);
        }

        stepX.set(xStepValue);
        stepY.set(yStepValue);
        if (!isEnable) {
            toggle();
        }
    }, [isEnable, stepX, stepY, toggle, xStepInput, yStepInput]);

    const resetInput = useCallback(() => {
        setXStepInput(stepX.value);
        setYStepInput(stepY.value);
    }, [stepX.value, stepY.value]);

    return <Fragment>
        {
            lineType !== LineType.ZSlice
            &&
            <Fragment>
                <IconButton
                    className={className}
                    style={{color:tenantConfig?.main.palette.primary.contrastText}}
                    title='Enable graticule grid lines. Right click for extra options'
                    onClick={handleMenu}
                    ref={iconButtonRef}
                    id='graticule'
                >
                    <Grid />
                </IconButton>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={openDetail}
                    onClose={handleClose}
                >
                    <MenuItem>
                        <FormControlLabel
                            label={<Typography color='white'>{ t('graticule.enable') }</Typography>}
                            control={<Switch checked={isEnable} onChange={toggle}/>}
                        />
                    </MenuItem>
                    <MenuItem>
                        <FormControlLabel
                            label={t('graticule.enableLines')}
                            style={{color: 'white'}}
                            control={<Checkbox checked={line.value} onChange={evt => {line.set(evt.target.checked)}}/>} />

                    </MenuItem>
                    <Stack>
                        <div css={inputStyle} >
                            <MenuItem>
                                <TextField label='X Axis' value={xStepInput} onChange={(evt) => setXStepInput(parseInt(evt.target.value))} color='primary'/>
                            </MenuItem>
                            <MenuItem>
                                <TextField label='Y Axis' value={yStepInput} onChange={(evt) => setYStepInput(parseInt(evt.target.value))} color='primary'/>
                            </MenuItem>
                        </div>
                        <Stack direction={'row'} gap={1} justifyContent={'center'}>
                            <IconButton onClick={() => resetInput()}><RestartAltIcon style={{color: 'white'}}></RestartAltIcon></IconButton>
                            <IconButton onClick={() => applySteps()}><OfflinePinIcon style={{color: 'white'}}/></IconButton>
                        </Stack>
                    </Stack>
                </Menu>
            </Fragment>
        }
    </Fragment>;
}

interface Props {
    dataGeometryLine: ILineString | null;
}